import type { Dispatch, RefObject, SetStateAction } from 'react'
import type { Socket } from 'socket.io-client'
import type { CurrentUserWithPermissions } from '@/shared/lib/workspace-permissions'
import type {
  PresenceChangedEvent,
  PresenceSnapshotEvent,
  WorkspacePresenceUser,
} from '@/features/workspace-presence/model/workspace-presence'
import { resolvePublicApiUrl } from '@/shared/lib/api-url'
import {
  PRESENCE_NAMESPACE,
  normalizeWorkspacePresenceUser,
} from '@/features/workspace-presence/model/workspace-presence'

type CurrentUser = CurrentUserWithPermissions
type PresenceUsersSetter = Dispatch<
  SetStateAction<Array<WorkspacePresenceUser>>
>

export type PresenceSocketContext = {
  currentDocumentCollectionId: string | null
  currentUser: CurrentUser | null
  pathname: string
  recordId: string | null
}

export function buildPresenceSocketUrl(apiUrl: string) {
  return resolvePublicApiUrl(apiUrl, PRESENCE_NAMESPACE)
}

export function buildPresenceSocketOptions() {
  return {
    autoConnect: false,
    transports: ['websocket'],
    withCredentials: true,
  }
}

function mergePresenceUser({
  currentUsers,
  nextUser,
}: {
  currentUsers: Array<WorkspacePresenceUser>
  nextUser: WorkspacePresenceUser
}) {
  if (nextUser.status === 'offline') {
    return currentUsers.filter(
      (presence) => presence.userId !== nextUser.userId,
    )
  }

  return [
    ...currentUsers.filter((presence) => presence.userId !== nextUser.userId),
    nextUser,
  ]
}

function joinPresenceSocket(input: {
  documentCollectionId: string | null
  currentUser: CurrentUser
  pathname: string
  recordId: string | null
  sessionId: string
  socket: Socket
}) {
  input.socket.emit('presence.join', {
    documentCollectionId: input.documentCollectionId,
    recordId: input.recordId,
    route: input.pathname,
    sessionId: input.sessionId,
    workspaceId: input.currentUser.workspaceId,
  })
}

function createDisconnectClearTimer(input: {
  disconnectStaleMs: number
  setPresenceUsers: PresenceUsersSetter
}) {
  let timeoutId: number | null = null

  function clear() {
    if (timeoutId === null) {
      return
    }

    window.clearTimeout(timeoutId)
    timeoutId = null
  }

  function schedule() {
    clear()
    timeoutId = window.setTimeout(() => {
      input.setPresenceUsers([])
      timeoutId = null
    }, input.disconnectStaleMs)
  }

  return { clear, schedule }
}

export function registerPresenceSocketHandlers(input: {
  currentUser: CurrentUser
  disconnectStaleMs: number
  sessionId: string
  socketContextRef: RefObject<PresenceSocketContext>
  setPresenceUsers: PresenceUsersSetter
  socket: Socket
}) {
  const disconnectClearTimer = createDisconnectClearTimer(input)

  function handleSnapshot(event: PresenceSnapshotEvent) {
    if (event.workspaceId !== input.currentUser.workspaceId) {
      return
    }

    disconnectClearTimer.clear()
    input.setPresenceUsers(
      event.workspaceUsers
        .map(normalizeWorkspacePresenceUser)
        .filter((presence) => presence.userId !== input.currentUser.id),
    )
  }

  function handleChanged(event: PresenceChangedEvent) {
    if (
      event.workspaceId !== input.currentUser.workspaceId ||
      event.user.userId === input.currentUser.id
    ) {
      return
    }

    disconnectClearTimer.clear()
    input.setPresenceUsers((currentUsers) =>
      mergePresenceUser({
        currentUsers,
        nextUser: normalizeWorkspacePresenceUser(event.user),
      }),
    )
  }

  function handleUnavailable() {
    disconnectClearTimer.clear()
    input.setPresenceUsers([])
  }

  function handleDisconnect() {
    disconnectClearTimer.schedule()
  }

  function handleConnect() {
    disconnectClearTimer.clear()

    const socketContext = input.socketContextRef.current

    if (!socketContext.currentUser) {
      return
    }

    joinPresenceSocket({
      documentCollectionId: socketContext.currentDocumentCollectionId,
      currentUser: socketContext.currentUser,
      pathname: socketContext.pathname,
      recordId: socketContext.recordId,
      sessionId: input.sessionId,
      socket: input.socket,
    })
  }

  input.socket.on('connect', handleConnect)
  input.socket.on('disconnect', handleDisconnect)
  input.socket.on('presence.changed', handleChanged)
  input.socket.on('presence.snapshot', handleSnapshot)
  input.socket.on('presence.unavailable', handleUnavailable)

  return () => {
    disconnectClearTimer.clear()
    input.socket.off('connect', handleConnect)
    input.socket.off('disconnect', handleDisconnect)
    input.socket.off('presence.changed', handleChanged)
    input.socket.off('presence.snapshot', handleSnapshot)
    input.socket.off('presence.unavailable', handleUnavailable)
  }
}
